import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSwipeDown } from '../../../hooks/useSwipeDown.js';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import type { ToolSlotResponse, ToolStatus } from '@bridgewerk/shared';
import { Badge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { useToolLife } from '../../dashboard/hooks/useToolLife.js';
import { AssignToolModal } from '../../dashboard/components/AssignToolModal.js';
import { AddPocketModal } from '../components/AddPocketModal.js';
import { CheckoutModal } from '../components/CheckoutModal.js';
import { useUnassignTool, useSwapToolSlots } from '../../tooling/hooks/useToolLibrary.js';
import { useOutstandingCheckouts } from '../../custody/hooks/useCustody.js';

export interface ToolsTabProps {
  readonly shopId: string | null;
  readonly machineId: string;
}

const STATUS_COLOR: Record<ToolStatus, string> = {
  change: 'var(--color-red)',
  order:  'var(--color-orange)',
  watch:  'var(--color-yellow)',
  good:   'var(--color-green)',
};

const STATUS_TONE: Record<ToolStatus, 'critical' | 'warning' | 'info' | 'opportunity'> = {
  change: 'critical',
  order:  'warning',
  watch:  'info',
  good:   'opportunity',
};

const STATUS_LABEL: Record<ToolStatus, string> = {
  change: 'Change',
  order:  'Order',
  watch:  'Watch',
  good:   'Good',
};

function pocketLabel(n: number): string {
  return `T${String(n).padStart(2, '0')}`;
}

// Lift the drag ghost above the thumb on touch screens
const touchOffsetModifier: Modifier = ({ transform, activatorEvent }) => {
  if (activatorEvent instanceof PointerEvent && activatorEvent.pointerType === 'touch') {
    return { ...transform, y: transform.y - 120 };
  }
  return { ...transform, x: transform.x + 12, y: transform.y + 12 };
};

export function ToolsTab({ shopId, machineId }: ToolsTabProps): JSX.Element {
  const { data, isLoading, isError, refetch } = useToolLife(shopId, machineId);
  const { data: checkouts } = useOutstandingCheckouts(shopId);
  const [assigning, setAssigning]     = useState<ToolSlotResponse | null>(null);
  const [checkingOut, setCheckingOut] = useState<ToolSlotResponse | null>(null);
  const [addingPocket, setAddingPocket] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ToolSlotResponse | null>(null);

  const unassign = useUnassignTool(shopId);
  const swap     = useSwapToolSlots(shopId);

  // Set of slot numbers currently checked out on this machine
  const checkedOutSlots = new Set(
    (checkouts ?? [])
      .filter(c => c.from_machine_id === machineId)
      .map(c => c.from_slot),
  );

  // 0.25s hold anywhere on the card activates drag
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const tools = data?.tools.slice().sort((a, b) => a.tool_number - b.tool_number) ?? [];
  const activeSlot = tools.find(t => t.tool_number === activeId) ?? null;

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !shopId) return;
    const fromSlot = tools.find(t => t.tool_number === (active.id as number));
    const toSlot   = tools.find(t => t.tool_number === (over.id as number)) ?? null;
    if (!fromSlot?.tool_definition) return;
    void swap.mutateAsync({ machineId, fromSlot, toSlot });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">Tool Turret</h2>
          {data && data.tools.length > 0 && (
            <p className="mt-0.5 text-[10px] font-mono text-muted">
              {data.tools.length} pocket{data.tools.length !== 1 ? 's' : ''} configured
              {tools.some(t => t.tool_definition) && (
                <span className="ml-2 text-border">· hold to drag &amp; swap</span>
              )}
            </p>
          )}
        </div>
        <Button variant="primary" onClick={() => setAddingPocket(true)}>+ Add pocket</Button>
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-28 rounded-md" />)}
        </div>
      )}

      {!isLoading && isError && (
        <div className="rounded-md border border-border bg-card p-6 text-center text-xs font-mono text-text-dim">
          <p>Failed to load tool data.</p>
          <button onClick={refetch} className="mt-2 text-accent underline">Retry</button>
        </div>
      )}

      {!isLoading && !isError && data && data.tools.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm font-mono text-text-dim">No pockets configured yet.</p>
          <p className="mt-1 text-[11px] font-mono text-muted">
            Click &ldquo;Add pocket&rdquo; to assign a tool from the library to a turret position.
          </p>
        </div>
      )}

      {!isLoading && !isError && tools.length > 0 && (
        <DndContext
          sensors={sensors}
          onDragStart={e => setActiveId(e.active.id as number)}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {tools.map(slot => (
              <DndPocketCard
                key={slot.tool_number}
                slot={slot}
                isDragging={slot.tool_number === activeId}
                isCheckedOut={checkedOutSlots.has(slot.tool_number)}
                onAssign={() => setAssigning(slot)}
                onDetail={() => setDetail(slot)}
                onRemove={() => void unassign.mutateAsync({ machineId, toolNumber: slot.tool_number })}
              />
            ))}
          </div>

          <DragOverlay modifiers={[touchOffsetModifier]}>
            {activeSlot && (
              <div className="rotate-1 shadow-2xl scale-105 select-none">
                <PocketCard slot={activeSlot} onAssign={() => {}} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {assigning && shopId && (
        <AssignToolModal
          shopId={shopId}
          machineId={machineId}
          slot={assigning}
          onClose={() => setAssigning(null)}
        />
      )}

      {addingPocket && shopId && (
        <AddPocketModal
          shopId={shopId}
          machineId={machineId}
          existingPockets={data?.tools.map(t => t.tool_number) ?? []}
          onClose={() => setAddingPocket(false)}
        />
      )}

      {detail && (
        <SlotDetailSheet
          slot={detail}
          isCheckedOut={checkedOutSlots.has(detail.tool_number)}
          onAssign={() => { setAssigning(detail); setDetail(null); }}
          onCheckout={() => { setCheckingOut(detail); setDetail(null); }}
          onRemove={() => {
            void unassign.mutateAsync({ machineId, toolNumber: detail.tool_number });
            setDetail(null);
          }}
          onClose={() => setDetail(null)}
        />
      )}

      {checkingOut && shopId && (
        <CheckoutModal
          shopId={shopId}
          machineId={machineId}
          slot={checkingOut.tool_number}
          onClose={() => setCheckingOut(null)}
        />
      )}
    </div>
  );
}

// ── DnD wrapper ───────────────────────────────────────────────────────────────

function DndPocketCard({
  slot,
  isDragging,
  isCheckedOut,
  onAssign,
  onDetail,
  onRemove,
}: {
  readonly slot: ToolSlotResponse;
  readonly isDragging: boolean;
  readonly isCheckedOut: boolean;
  readonly onAssign: () => void;
  readonly onDetail: () => void;
  readonly onRemove: () => void;
}): JSX.Element {
  const canDrag = slot.tool_definition !== null && !isCheckedOut;
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
    id: slot.tool_number,
    disabled: !canDrag,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: slot.tool_number });

  return (
    <div
      ref={node => { setDragRef(node); setDropRef(node); }}
      {...(canDrag ? listeners : {})}
      {...(canDrag ? attributes : {})}
      className={[
        'rounded-md transition-opacity select-none',
        canDrag ? 'cursor-grab active:cursor-grabbing' : '',
        isDragging ? 'opacity-25' : '',
        isOver && !isDragging ? 'ring-2 ring-accent' : '',
      ].join(' ')}
    >
      <PocketCard
        slot={slot}
        isCheckedOut={isCheckedOut}
        onAssign={onAssign}
        onDetail={onDetail}
        onRemove={onRemove}
      />
    </div>
  );
}

// ── Pocket card ───────────────────────────────────────────────────────────────

function PocketCard({
  slot,
  isCheckedOut = false,
  onAssign,
  onDetail,
  onRemove,
}: {
  readonly slot: ToolSlotResponse;
  readonly isCheckedOut?: boolean;
  readonly onAssign: () => void;
  readonly onDetail?: () => void;
  readonly onRemove?: () => void;
}): JSX.Element {
  const hasIdentity = slot.tool_definition !== null;
  const barColor = isCheckedOut ? 'var(--color-muted)' : STATUS_COLOR[slot.status];

  return (
    <div
      className={['rounded-md border border-border bg-card overflow-hidden flex flex-col group', isCheckedOut ? 'opacity-60' : ''].join(' ')}
      style={hasIdentity ? { borderTopColor: barColor, borderTopWidth: 2 } : { borderTopWidth: 2 }}
    >
      <button
        type="button"
        onPointerDown={e => e.stopPropagation()}
        onClick={hasIdentity && onDetail ? onDetail : undefined}
        className={['text-left flex-1 flex flex-col', hasIdentity ? 'cursor-pointer' : ''].join(' ')}
      >
        <div className="px-3 pt-3 pb-2 flex items-start justify-between gap-1">
          <span className="font-mono text-lg font-semibold leading-none select-none">{pocketLabel(slot.tool_number)}</span>
          {isCheckedOut
            ? <Badge tone="info">Out</Badge>
            : hasIdentity && <Badge tone={STATUS_TONE[slot.status]}>{STATUS_LABEL[slot.status]}</Badge>}
        </div>

        <div className="px-3 pb-1 flex-1">
          {hasIdentity ? (
            <>
              <p className="text-xs font-mono truncate leading-snug">{slot.tool_definition!.description}</p>
              <p className="text-[10px] font-mono text-text-dim truncate">
                {slot.tool_definition!.type} · {slot.tool_definition!.material}
                {slot.tool_definition!.diameter_mm != null ? ` · ${slot.tool_definition!.diameter_mm} mm` : ''}
              </p>
            </>
          ) : (
            <p className="text-[11px] font-mono text-muted italic">No identity assigned</p>
          )}
        </div>

        {hasIdentity && !isCheckedOut && (
          <div className="px-3 pb-2 mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono text-text-dim">Life</span>
              <span className="text-[10px] font-mono">{slot.life_percent}%</span>
            </div>
            <div className="h-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <div
                className="h-full rounded-full transition-[width] duration-700"
                style={{ width: `${slot.life_percent}%`, backgroundColor: barColor }}
              />
            </div>
          </div>
        )}
      </button>

      <div className="border-t border-border px-3 py-2 flex items-center justify-between">
        {isCheckedOut ? (
          <span className="text-[11px] font-mono text-muted italic">Checked out</span>
        ) : (
          <>
            <button
              type="button"
              onPointerDown={e => e.stopPropagation()}
              onClick={onAssign}
              className="text-[11px] font-mono text-accent hover:underline"
            >
              {hasIdentity ? 'Change tool →' : 'Assign tool →'}
            </button>
            {hasIdentity && onRemove && <RemoveButton onRemove={onRemove} />}
          </>
        )}
      </div>
    </div>
  );
}

// ── Slot detail sheet ─────────────────────────────────────────────────────────

function SlotDetailSheet({
  slot,
  isCheckedOut,
  onAssign,
  onCheckout,
  onRemove,
  onClose,
}: {
  readonly slot: ToolSlotResponse;
  readonly isCheckedOut: boolean;
  readonly onAssign: () => void;
  readonly onCheckout: () => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const swipe = useSwipeDown(onClose);
  const def = slot.tool_definition;
  const barColor = STATUS_COLOR[slot.status];

  const fields: Array<[string, string]> = [
    ['Pocket', pocketLabel(slot.tool_number)],
    ['Status', STATUS_LABEL[slot.status]],
    ['Life', `${slot.life_percent}%`],
    ...(def ? [
      ['Type', def.type] as [string, string],
      ['Material', def.material] as [string, string],
      ...(def.diameter_mm != null       ? [['Diameter', `${def.diameter_mm} mm`] as [string, string]] : []),
      ...(def.manufacturer              ? [['Manufacturer', def.manufacturer] as [string, string]] : []),
      ...(def.part_number               ? [['Part No.', def.part_number] as [string, string]] : []),
      ...(def.expected_life_cuts != null    ? [['Life (cuts)', String(def.expected_life_cuts)] as [string, string]] : []),
      ...(def.expected_life_minutes != null ? [['Life (min)', String(def.expected_life_minutes)] as [string, string]] : []),
      ...(def.notes                     ? [['Notes', def.notes] as [string, string]] : []),
    ] : []),
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-xl border-t md:border border-border overflow-hidden flex flex-col"
        style={{
          backgroundColor: 'rgba(11,16,23,0.98)',
          animation: 'sheet-up 0.32s cubic-bezier(0.32,0.72,0,1)',
          maxHeight: '82vh',
          paddingBottom: 'env(safe-area-inset-bottom)',
          ...swipe.style,
        }}
      >
        {/* Drag handle (mobile) — swipe down to dismiss */}
        <div
          className="flex justify-center pt-3 pb-1 md:hidden shrink-0 touch-none cursor-grab"
          {...swipe.handlers}
        >
          <div className="h-1 w-10 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="px-5 py-3 border-b border-border flex items-center justify-between shrink-0" style={{ borderTopColor: barColor, borderTopWidth: 2 }}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-mono text-xl font-semibold leading-none">{pocketLabel(slot.tool_number)}</span>
            <Badge tone={STATUS_TONE[slot.status]}>{STATUS_LABEL[slot.status]}</Badge>
            {def && <p className="text-sm font-mono text-text truncate">{def.description}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-text-dim hover:text-text shrink-0 ml-3 leading-none">✕</button>
        </div>

        {/* Fields */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {def ? (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              {fields.map(([label, value]) => (
                <div key={label} className={label === 'Notes' ? 'col-span-2' : ''}>
                  <dt className="text-[9px] font-mono uppercase tracking-widest text-muted">{label}</dt>
                  <dd className="text-sm font-mono text-text mt-0.5 break-words leading-snug">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm font-mono text-muted italic text-center py-4">No tool identity assigned to this pocket.</p>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 px-5 py-4 border-t border-border flex gap-2">
          {isCheckedOut ? (
            <p className="flex-1 text-sm font-mono text-muted italic self-center">
              Tool is currently checked out.
            </p>
          ) : (
            <>
              <button
                type="button"
                onClick={onAssign}
                className="flex-1 rounded-md bg-accent/10 border border-accent/30 py-2.5 text-sm font-mono text-accent hover:bg-accent/20 transition-colors"
              >
                {def ? 'Change tool' : 'Assign tool'}
              </button>
              {def && (
                <button
                  type="button"
                  onClick={onCheckout}
                  className="flex-1 rounded-md border border-yellow/30 bg-yellow/10 py-2.5 text-sm font-mono text-yellow hover:bg-yellow/20 transition-colors"
                >
                  Check Out
                </button>
              )}
              {def && (confirming ? (
                <>
                  <button
                    type="button"
                    onClick={() => { onRemove(); setConfirming(false); }}
                    className="flex-1 rounded-md bg-red/15 border border-red/30 py-2.5 text-sm font-mono text-red hover:bg-red/25 transition-colors"
                  >
                    Confirm remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="px-4 rounded-md border border-border py-2.5 text-sm font-mono text-text-dim hover:text-text transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="flex-1 rounded-md border border-border py-2.5 text-sm font-mono text-text-dim hover:text-red hover:border-red/40 transition-colors"
                >
                  Remove
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RemoveButton({ onRemove }: { readonly onRemove: () => void }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <div className="flex gap-1.5 items-center">
        <span className="text-[10px] font-mono text-text-dim select-none">Remove?</span>
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={() => { onRemove(); setConfirming(false); }}
          className="text-[10px] font-mono text-red"
        >
          Yes
        </button>
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={() => setConfirming(false)}
          className="text-[10px] font-mono text-text-dim"
        >
          No
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onPointerDown={e => e.stopPropagation()}
      onClick={() => setConfirming(true)}
      className="text-[10px] font-mono text-muted hover:text-red opacity-0 group-hover:opacity-100 transition-opacity"
    >
      Remove
    </button>
  );
}
